import React, { useState, useEffect, useRef } from "react";
import moment from "moment";
import { CSSTransition } from "react-transition-group";
import { useTranslation } from "react-i18next";

import helpChat from "../../services/HelpChat";
import helpLibrary from "../../services/HelpLibrary";
import chatService from "../../services/Chat";
import ChatMessage, {
  ChatMessageLoading,
} from "../Documents/Panels/Chats/ChatMessage";
import closeIcon from "../../images/icons/svg/close-black.svg";

import styles from "./HelpChatPanel.module.scss";

const HELP_CENTRE_URL = "https://help.papercurve.com";
const EXAMPLE_QUESTION_KEYS = ["example_1", "example_2", "example_3"];

/**
 * The in-app help chat: an Ask Paige conversation over the installation's help
 * library, docked to the right edge of the viewport.
 *
 * Mounted once in App.js for signed-in users. It renders nothing until the
 * Help menu item opens it, and nothing at all when GET /help_library says the
 * installation has no help library. Conversation state lives in this component
 * (which stays mounted while the panel is closed) and in the HelpChat store, so
 * reopening the panel, or opening it on another page, returns to the same
 * conversation.
 */
const HelpChatPanel = () => {
  const { t } = useTranslation();

  const [isOpen, setIsOpen] = useState(helpChat.isOpen());
  const [library, setLibrary] = useState(helpLibrary.get());
  const [view, setView] = useState("chat");
  const [sessionId, setSessionId] = useState(helpChat.getSessionId());
  const [messages, setMessages] = useState([]);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [sendFailed, setSendFailed] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [sessions, setSessions] = useState([]);
  const [isLoadingSessions, setIsLoadingSessions] = useState(false);
  // Bumped whenever the question box should take focus: on opening the panel
  // from the menu and on switching conversations, but not on every page load
  // while the panel happens to be open, which would steal focus from the page.
  const [focusRequest, setFocusRequest] = useState(0);
  const [welcomeTime] = useState(function () {
    return new Date().toISOString();
  });

  const panelRef = useRef(null);
  const messagesRef = useRef(null);
  const textareaRef = useRef(null);
  // The session whose messages are currently displayed, so a session that was
  // just created for an optimistic message is not reloaded from the server.
  const loadedSessionIdRef = useRef(null);

  const isVisible = isOpen && Boolean(library);
  const isHistoryView = view === "history";

  // Follow the Help menu item.
  useEffect(function () {
    return helpChat.subscribe(function (nextIsOpen) {
      setIsOpen(nextIsOpen);
      if (nextIsOpen) {
        requestFocus();
      }
    });
  }, []);

  // Find out whether this installation has a help library.
  useEffect(function () {
    let cancelled = false;
    helpLibrary.load().then(function (loadedLibrary) {
      if (!cancelled) {
        setLibrary(loadedLibrary);
      }
    });
    return function () {
      cancelled = true;
    };
  }, []);

  // Load the stored conversation the first time the panel shows it.
  useEffect(
    function () {
      const hasSession = sessionId !== null;
      const isAlreadyLoaded = loadedSessionIdRef.current === sessionId;
      if (!isVisible || !hasSession || isAlreadyLoaded) {
        return;
      }
      loadedSessionIdRef.current = sessionId;
      loadSession(sessionId);
    },
    [isVisible, sessionId]
  );

  // Escape closes the panel.
  useEffect(
    function () {
      if (!isVisible) {
        return;
      }
      const handleKeyDown = function (event) {
        if (event.key === "Escape") {
          helpChat.close();
        }
      };
      document.addEventListener("keydown", handleKeyDown);
      return function () {
        document.removeEventListener("keydown", handleKeyDown);
      };
    },
    [isVisible]
  );

  // Focus the question box when asked to, once it is rendered and enabled.
  useEffect(
    function () {
      const hasRequest = focusRequest > 0;
      const textarea = textareaRef.current;
      if (hasRequest && textarea && !textarea.disabled) {
        textarea.focus();
      }
    },
    [focusRequest, isLoadingMessages, isHistoryView, isVisible]
  );

  // Keep the latest message in view. The list is scrolled directly rather than
  // with scrollIntoView, which could also scroll the page under the panel.
  useEffect(
    function () {
      const list = messagesRef.current;
      if (list) {
        list.scrollTop = list.scrollHeight;
      }
    },
    [messages, isSending, isVisible, isHistoryView]
  );

  // Grow the question box with its content, up to a few lines.
  useEffect(
    function () {
      const textarea = textareaRef.current;
      if (textarea) {
        textarea.style.height = "auto";
        textarea.style.height = Math.min(textarea.scrollHeight, 120) + "px";
      }
    },
    [inputValue, isVisible, isHistoryView]
  );

  const requestFocus = function () {
    setFocusRequest(function (previous) {
      return previous + 1;
    });
  };

  /**
   * Loads one conversation with its messages. A conversation that no longer
   * exists is forgotten and the panel starts fresh.
   */
  const loadSession = function (id) {
    setIsLoadingMessages(true);
    setSendFailed(false);
    chatService
      .getSession(id)
      .then(function (response) {
        setMessages(response.data.messages || []);
      })
      .catch(function (error) {
        const isGone = error && error.response && error.response.status === 404;
        if (!isGone) {
          console.error("Error loading help conversation:", error);
        }
        startNewConversation();
      })
      .finally(function () {
        setIsLoadingMessages(false);
      });
  };

  /**
   * Lists the person's previous help conversations.
   */
  const loadSessions = function () {
    setIsLoadingSessions(true);
    chatService
      .getSessionsForLibrary(library.id)
      .then(function (response) {
        setSessions(response.data || []);
      })
      .catch(function (error) {
        console.error("Error loading help conversations:", error);
        setSessions([]);
      })
      .finally(function () {
        setIsLoadingSessions(false);
      });
  };

  /**
   * Forgets the current conversation. The next question creates a new one.
   */
  const startNewConversation = function () {
    loadedSessionIdRef.current = null;
    helpChat.setSessionId(null);
    setSessionId(null);
    setMessages([]);
    setSendFailed(false);
    setView("chat");
    requestFocus();
  };

  const showHistory = function () {
    setView("history");
    loadSessions();
  };

  const showConversation = function () {
    setView("chat");
    requestFocus();
  };

  const selectSession = function (id) {
    helpChat.setSessionId(id);
    setSessionId(id);
    setView("chat");
    requestFocus();
  };

  /**
   * Sends a question to Paige in the current conversation, creating the
   * conversation first when there is none yet.
   */
  const sendQuestion = function (text) {
    const query = text.trim();
    if (query === "" || isSending || !library) {
      return;
    }

    const optimisticMessage = {
      id: "pending-" + Date.now(),
      role: "user",
      content: query,
      created_at: new Date().toISOString(),
    };

    setIsSending(true);
    setSendFailed(false);
    setInputValue("");
    setMessages(function (previous) {
      return previous.concat([optimisticMessage]);
    });

    const ensureSession = function () {
      if (sessionId !== null) {
        return Promise.resolve(sessionId);
      }
      return chatService.createSession().then(function (response) {
        const newSessionId = response.data.id;
        loadedSessionIdRef.current = newSessionId;
        helpChat.setSessionId(newSessionId);
        setSessionId(newSessionId);
        return newSessionId;
      });
    };

    ensureSession()
      .then(function (currentSessionId) {
        const context = [{ type: "Library", id: library.id }];
        return chatService.sendMessage(currentSessionId, query, context);
      })
      .then(function (response) {
        setMessages(function (previous) {
          const withoutOptimistic = previous.filter(function (message) {
            return message.id !== optimisticMessage.id;
          });
          return withoutOptimistic.concat([
            response.data.user_message,
            response.data.assistant_message,
          ]);
        });
      })
      .catch(function (error) {
        console.error("Error sending help question:", error);
        setMessages(function (previous) {
          return previous.filter(function (message) {
            return message.id !== optimisticMessage.id;
          });
        });
        setInputValue(query);
        setSendFailed(true);
      })
      .finally(function () {
        setIsSending(false);
      });
  };

  const handleSend = function () {
    sendQuestion(inputValue);
  };

  const handleInputChange = function (event) {
    setInputValue(event.target.value);
  };

  const handleKeyDown = function (event) {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
  };

  const welcomeMessage = {
    id: "welcome",
    role: "assistant",
    content: t("help_chat.welcome"),
    citations: null,
    created_at: welcomeTime,
  };

  const renderWelcome = function () {
    return (
      <div className={styles.welcome}>
        <ChatMessage
          message={welcomeMessage}
          isLibraryContext={true}
          variant="help"
        />
        <div className={styles.examples}>
          {EXAMPLE_QUESTION_KEYS.map(function (key) {
            const question = t("help_chat." + key);
            return (
              <button
                key={key}
                type="button"
                className={styles.exampleChip}
                onClick={function () {
                  sendQuestion(question);
                }}
              >
                {question}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  const renderConversation = function () {
    const isEmpty = messages.length === 0 && !isSending;
    return (
      <>
        <div className={styles.messages} ref={messagesRef} aria-live="polite">
          {isLoadingMessages ? (
            <div className={styles.centered}>
              <ChatMessageLoading variant="help" />
            </div>
          ) : (
            <>
              {isEmpty && renderWelcome()}
              {messages.map(function (message) {
                return (
                  <ChatMessage
                    key={message.id}
                    message={message}
                    isLibraryContext={true}
                    variant="help"
                  />
                );
              })}
              {isSending && <ChatMessageLoading variant="help" />}
            </>
          )}
        </div>

        <div className={styles.inputContainer}>
          {sendFailed && (
            <div className={styles.sendError} role="alert">
              {t("help_chat.send_failed")}
            </div>
          )}
          <div className={styles.inputRow}>
            <textarea
              ref={textareaRef}
              className={styles.textarea}
              value={inputValue}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              placeholder={t("help_chat.placeholder")}
              aria-label={t("help_chat.placeholder")}
              disabled={isSending || isLoadingMessages}
              rows={1}
            />
            <button
              type="button"
              className={styles.sendButton}
              onClick={handleSend}
              disabled={inputValue.trim() === "" || isSending}
            >
              {t("help_chat.send")}
            </button>
          </div>
        </div>
      </>
    );
  };

  const renderHistory = function () {
    return (
      <div className={styles.history}>
        <div className={styles.historyTitle}>
          {t("help_chat.previous_conversations")}
        </div>
        {isLoadingSessions ? (
          <div className={styles.historyEmpty}>{t("help_chat.loading")}</div>
        ) : sessions.length === 0 ? (
          <div className={styles.historyEmpty}>
            {t("help_chat.no_previous_conversations")}
          </div>
        ) : (
          <ul className={styles.sessionList}>
            {sessions.map(function (session) {
              const isActive = session.id === sessionId;
              return (
                <li key={session.id}>
                  <button
                    type="button"
                    className={
                      isActive
                        ? styles.sessionItem + " " + styles.sessionItemActive
                        : styles.sessionItem
                    }
                    onClick={function () {
                      selectSession(session.id);
                    }}
                  >
                    <span className={styles.sessionTitle}>
                      {session.title || t("help_chat.untitled_conversation")}
                    </span>
                    <span className={styles.sessionDate}>
                      {moment(session.updated_at).fromNow()}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    );
  };

  return (
    <CSSTransition
      in={isVisible}
      timeout={300}
      classNames={{
        enter: styles.enter,
        enterActive: styles.enterActive,
        exit: styles.exit,
        exitActive: styles.exitActive,
      }}
      nodeRef={panelRef}
      unmountOnExit
    >
      <aside
        ref={panelRef}
        id="help_chat_panel"
        className={styles.panel}
        aria-label={t("help_chat.title")}
      >
        <div className={styles.header}>
          <div className={styles.avatar} aria-hidden="true">
            P
          </div>
          <div className={styles.headerText}>
            <div className={styles.title}>{t("help_chat.title")}</div>
            <div className={styles.subtitle}>{t("help_chat.subtitle")}</div>
          </div>
          <button
            type="button"
            className={styles.closeButton}
            onClick={helpChat.close}
            aria-label={t("help_chat.close")}
          >
            <img src={closeIcon} alt="" />
          </button>
        </div>

        <div className={styles.toolbar}>
          {isHistoryView ? (
            <button
              type="button"
              className={styles.toolbarButton}
              onClick={showConversation}
            >
              {t("help_chat.back_to_conversation")}
            </button>
          ) : (
            <button
              type="button"
              className={styles.toolbarButton}
              onClick={showHistory}
              disabled={isSending}
            >
              {t("help_chat.previous_conversations")}
            </button>
          )}
          <button
            type="button"
            className={styles.toolbarButton}
            onClick={startNewConversation}
            disabled={isSending}
          >
            {t("help_chat.new_conversation")}
          </button>
        </div>

        {isHistoryView ? renderHistory() : renderConversation()}

        <div className={styles.footer}>
          <a href={HELP_CENTRE_URL} target="_blank" rel="noopener noreferrer">
            {t("help_chat.browse_help_centre")}
          </a>
        </div>
      </aside>
    </CSSTransition>
  );
};

export default HelpChatPanel;
